/**
 * Scoring system — calculates scores from run data.
 *
 * Implements the scoring formula from docs/scoring-system.md:
 * final = (completion + stars + time + elegance + fragments) × convergence
 */

import type { Difficulty } from '@/types';

// ─── Score Constants ───────────────────────────────────────

const DIFFICULTY_BASE: Record<Difficulty, number> = {
  easy: 1000,
  normal: 2500,
  hard: 5000,
  insane: 10000,
  triggered: 25000,
};

const STAR_BONUS = 500;
const FRAGMENT_BONUS = 1000;

/** Convergence combo multiplier tiers */
function getConvergenceMultiplier(grazeCount: number): number {
  if (grazeCount >= 51) return 5.0;
  if (grazeCount >= 31) return 4.0;
  if (grazeCount >= 16) return 3.0;
  if (grazeCount >= 6) return 2.0;
  if (grazeCount >= 1) return 1.5;
  return 1.0;
}

/** Elegance bonus based on input efficiency */
function getEleganceBonus(actualInputs: number, optimalInputs: number): { bonus: number; ratio: number; tier: string } {
  if (optimalInputs <= 0 || actualInputs <= 0) return { bonus: 0, ratio: 0, tier: 'Standard' };

  const ratio = Math.min(1, optimalInputs / actualInputs);

  if (ratio >= 1.0) return { bonus: 5000, ratio, tier: 'Perfect' };
  if (ratio > 0.8) return { bonus: 3000, ratio, tier: 'Elegant' };
  if (ratio > 0.6) return { bonus: 1500, ratio, tier: 'Clean' };
  if (ratio > 0.4) return { bonus: 500, ratio, tier: 'Standard' };
  return { bonus: 0, ratio, tier: 'Frantic' };
}

/** Time bonus — faster = more points */
function getTimeBonus(actualTimeMs: number, parTimeMs: number, difficulty: Difficulty): number {
  if (actualTimeMs >= parTimeMs) return 0;
  const ratio = (parTimeMs - actualTimeMs) / parTimeMs;
  const multiplier = DIFFICULTY_BASE[difficulty] / 1000;
  return Math.floor(ratio * multiplier * 1000);
}

// ─── Run Data ──────────────────────────────────────────────

/** Data collected during a single run attempt */
export interface RunData {
  levelId: string;
  difficulty: Difficulty;
  completed: boolean;
  practiceUsedThisRun: boolean;
  timeMs: number;
  parTimeMs: number;
  optimalInputs: number;
  inputCount: number;
  starsCollected: [boolean, boolean, boolean];
  fragmentsCollected: boolean[];
  grazeCount: number;
  peakGrazeStreak: number;
  progress: number; // 0-1
}

/** Calculated score breakdown */
export interface ScoreBreakdown {
  completionScore: number;
  starBonus: number;
  starCount: number;
  timeBonus: number;
  eleganceBonus: number;
  eleganceRatio: number;
  eleganceTier: string;
  fragmentBonus: number;
  fragmentCount: number;
  convergenceMultiplier: number;
  grazeCount: number;
  subtotal: number;
  finalScore: number;
}

/** Calculate the full score breakdown from run data */
export function calculateScore(run: RunData): ScoreBreakdown {
  const completionScore = run.completed ? DIFFICULTY_BASE[run.difficulty] : 0;

  const starCount = run.starsCollected.filter(Boolean).length;
  const starBonus = starCount * STAR_BONUS;

  const timeBonus = run.completed
    ? getTimeBonus(run.timeMs, run.parTimeMs, run.difficulty)
    : 0;

  const { bonus: eleganceBonus, ratio: eleganceRatio, tier: eleganceTier } =
    getEleganceBonus(run.inputCount, run.optimalInputs);

  const fragmentCount = run.fragmentsCollected.filter(Boolean).length;
  const fragmentBonus = fragmentCount * FRAGMENT_BONUS;

  const convergenceMultiplier = getConvergenceMultiplier(run.peakGrazeStreak);

  const subtotal = completionScore + starBonus + timeBonus + eleganceBonus + fragmentBonus;
  const finalScore = Math.floor(subtotal * convergenceMultiplier);

  return {
    completionScore,
    starBonus,
    starCount,
    timeBonus,
    eleganceBonus,
    eleganceRatio,
    eleganceTier,
    fragmentBonus,
    fragmentCount,
    convergenceMultiplier,
    grazeCount: run.grazeCount,
    subtotal,
    finalScore,
  };
}
